import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  GatewayConnectivityDto,
  ProjectsApiService,
} from '../../../core/services/projects-api.service';

type PeriodPreset = '24h' | '7d' | '30d' | 'custom';

@Component({
  selector: 'app-gateway-connectivity-modal',
  standalone: false,
  templateUrl: './gateway-connectivity-modal.html',
  styleUrl: './gateway-connectivity-modal.scss',
})
export class GatewayConnectivityModal implements OnChanges {
  @Input({ required: true }) open = false;
  @Input({ required: true }) projectId = '';
  @Input({ required: true }) gatewaySerial = '';
  @Output() close = new EventEmitter<void>();

  protected data: GatewayConnectivityDto | null = null;
  protected errorMessage: string | null = null;
  protected loading = false;
  protected preset: PeriodPreset = '7d';
  protected customStart = '';
  protected customEnd = '';
  protected pageSizeOptions = [10, 25, 50];
  protected pageSize = 10;
  protected pageIndex = 0;

  constructor(
    private readonly projectsApi: ProjectsApiService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const shouldLoad = this.open && this.projectId && this.gatewaySerial && (
      changes['open'] || changes['projectId'] || changes['gatewaySerial']
    );
    if (!shouldLoad) return;

    this.preset = '7d';
    this.applyPreset();
  }

  protected get title() {
    return `Conectividade do gateway ${this.gatewaySerial}`;
  }

  protected get periodLabel() {
    if (!this.data) return 'Últimos 7 dias';
    return `${this.formatTimestamp(this.data.start)} até ${this.formatTimestamp(this.data.end)}`;
  }

  protected get uptimeLabel() {
    if (!this.data || this.data.uptime_percent === null) return '--';
    return `${this.data.uptime_percent.toFixed(2).replace('.', ',')}%`;
  }

  protected get pagedOutages() {
    const outages = this.data?.outages ?? [];
    return outages.slice(this.pageIndex * this.pageSize, (this.pageIndex + 1) * this.pageSize);
  }

  protected get pageCount() {
    const count = this.data?.outages.length ?? 0;
    return Math.max(1, Math.ceil(count / this.pageSize));
  }

  protected selectPreset(value: string) {
    this.preset = value as PeriodPreset;
    if (this.preset === 'custom') {
      this.setDefaultCustomRange();
      return;
    }
    this.applyPreset();
  }

  protected applyCustomRange() {
    const start = new Date(this.customStart).getTime();
    const end = new Date(this.customEnd).getTime();
    const maxRange = 90 * 24 * 60 * 60 * 1000;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || end - start > maxRange) {
      this.errorMessage = 'Selecione um período válido de até 3 meses.';
      return;
    }
    void this.load(start, end);
  }

  protected setPageSize(value: string) {
    const next = Number(value);
    if (!Number.isFinite(next) || next <= 0) return;
    this.pageSize = next;
    this.pageIndex = 0;
  }

  protected prevPage() {
    this.pageIndex = Math.max(0, this.pageIndex - 1);
  }

  protected nextPage() {
    this.pageIndex = Math.min(this.pageCount - 1, this.pageIndex + 1);
  }

  protected formatTimestamp(value: number | null) {
    if (!value) return 'Antes do período';
    const date = new Date(value);
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  protected formatReconnect(value: number | null) {
    return value ? this.formatTimestamp(value) : 'Sem retorno no período';
  }

  protected formatDuration(milliseconds: number) {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  protected requestClose() {
    this.close.emit();
  }

  private applyPreset() {
    const now = Date.now();
    const duration = this.preset === '24h'
      ? 24 * 60 * 60 * 1000
      : this.preset === '30d'
        ? 30 * 24 * 60 * 60 * 1000
        : 7 * 24 * 60 * 60 * 1000;
    void this.load(now - duration, now);
  }

  private setDefaultCustomRange() {
    const end = new Date();
    const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    this.customStart = this.toDateTimeLocal(start);
    this.customEnd = this.toDateTimeLocal(end);
  }

  private async load(start: number, end: number) {
    this.loading = true;
    this.errorMessage = null;
    this.pageIndex = 0;
    try {
      this.data = await firstValueFrom(
        this.projectsApi.getGatewayConnectivity(this.projectId, this.gatewaySerial, { start, end }),
      );
    } catch {
      this.data = null;
      this.errorMessage = 'Não foi possível carregar a conectividade do gateway.';
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private toDateTimeLocal(date: Date) {
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
